import type { AdvisorDockItem } from "../stores/advisor";
import type { AdvisorMessage } from "./advisor-types";
import type { RenderPayload } from "./agent-types";
import { apiRequest } from "./api";

/**
 * Wire shape for one persisted message coming back from
 * GET /api/agent/conversations/active. Mirrors the server's
 * `agentHistoryMessage` (see backend/internal/api/agent_history.go).
 *
 * `renders` is `unknown[]` on the wire and gets narrowed to typed
 * RenderPayload values during hydration — the existing render registry
 * already discriminates on the `type` discriminator so no extra parsing
 * is required.
 */
export interface AgentHistoryMessage {
  id: string;
  role: "user" | "advisor" | "assistant";
  text?: string;
  renders?: unknown[];
  profile_snapshot_id?: string;
  profile_recency_token?: string;
  followups?: string[];
  created_at: string;
}

export interface AgentHistoryResponse {
  conversation_id: string | null;
  messages: AgentHistoryMessage[];
}

/**
 * Fetches the caller's most recent active conversation and messages so
 * the chat UI can rehydrate across page reloads. Returns `{conversation_id:
 * null, messages: []}` when the user has no prior chat — callers should
 * treat that as the empty state.
 */
export async function fetchActiveAgentConversation(
  token: string,
  agentType = "readiness-advisor",
  signal?: AbortSignal,
): Promise<AgentHistoryResponse> {
  return apiRequest<AgentHistoryResponse>(
    `/api/agent/conversations/active?type=${encodeURIComponent(agentType)}`,
    { token, signal },
  );
}

/**
 * Type guard for renders coming back from the server. The wire is
 * `unknown[]`, but every RenderPayload in `agent-types.ts` carries a
 * literal `type` string. We accept the value when it has a recognised
 * discriminator and let the render registry handle anything else.
 */
const KNOWN_RENDER_TYPES: ReadonlySet<string> = new Set([
  "monthly_payment",
  "dti",
  "cash_to_close",
  "affordability_scenarios",
  "rent_vs_buy",
  "down_payment_comparison",
  "loan_product_preview",
  "rate_explanation",
]);

export function isRenderPayload(value: unknown): value is RenderPayload {
  if (!value || typeof value !== "object") return false;
  const type = (value as { type?: unknown }).type;
  return typeof type === "string" && KNOWN_RENDER_TYPES.has(type);
}

/**
 * seedFromHistory translates GET /api/agent/conversations/active's response
 * into the shape `useAdvisorStore.loadHistory` expects:
 *
 *   - `messages` are listed in chronological order (oldest first),
 *     mirroring the live in-memory order.
 *   - `dockItems` come back newest-first to match the store's
 *     `[newItem, ...rest]` unshift semantics. Each item carries the
 *     `profileSnapshotId` and `profileRecencyToken` from its source
 *     assistant message so the stale-widget badge can light up.
 *
 * Server-side roles are "user" / "assistant" but the client store uses
 * "user" / "advisor" — translate here.
 *
 * Shared by `AdvisorSessionProvider` (the one-shot-per-token hydration,
 * plan 435 M6) and `AdvisorLiveChat`'s explicit "Resume" flow, which
 * re-hydrates from the same wire shape after marking a historic
 * conversation active again.
 */
export function seedFromHistory(res: {
  conversation_id: string | null;
  messages: AgentHistoryMessage[];
}): {
  conversationId: string | null;
  messages: AdvisorMessage[];
  dockItems: AdvisorDockItem[];
} {
  const advisorMessages: AdvisorMessage[] = res.messages.map((m) => {
    const renders = (m.renders ?? []).filter(isRenderPayload);
    const role = m.role === "user" ? "user" : "advisor";
    return {
      id: m.id,
      role,
      text: m.text,
      renders: renders.length > 0 ? renders : undefined,
      // Surface a single render too for back-compat with bubble code that
      // reads message.render directly.
      render: renders[0],
      createdAt: m.created_at,
      profileSnapshotId: m.profile_snapshot_id,
      profileRecencyToken: m.profile_recency_token,
      followups: m.followups,
    };
  });

  // Newest-first dock seeding. Walk the server messages in reverse so the
  // most recent assistant payload sits at index 0 — matches the unshift
  // pattern in handleServerFrame's "render" case.
  const dockItems: AdvisorDockItem[] = [];
  for (let i = res.messages.length - 1; i >= 0; i--) {
    const m = res.messages[i];
    if (!m?.renders) continue;
    for (let j = m.renders.length - 1; j >= 0; j--) {
      const candidate = m.renders[j];
      if (!isRenderPayload(candidate)) continue;
      dockItems.push({
        id: `${m.id}-render-${j}`,
        payload: candidate as RenderPayload,
        renderIndex: j,
        sourceMessageId: m.id,
        updatedAt: m.created_at,
        profileSnapshotId: m.profile_snapshot_id,
        profileRecencyToken: m.profile_recency_token,
      });
    }
  }

  return {
    conversationId: res.conversation_id,
    messages: advisorMessages,
    dockItems,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// SILL-247: chat history & new-conversation flow.
//
// Wire shapes here mirror Go's `model.ConversationSummary` and the
// `agentConversationDetailResponse` / `agentResumeResponse` structs in
// backend/internal/api/agent_conversations.go. Keep them in sync — there's
// no codegen between the layers for these types (unlike the typed agent
// frame contract under agent-types.ts).
// ─────────────────────────────────────────────────────────────────────────

/** One row of GET /api/agent/conversations. Drives a sidebar entry. */
export interface AgentConversationSummary {
  id: string;
  agent_type: string;
  status: "active" | "closed";
  /** NULL for brand-new conversations with no derived title yet. */
  title?: string | null;
  message_count: number;
  /** ISO-8601; absent for empty conversations (no messages persisted). */
  last_message_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AgentConversationListResponse {
  conversations: AgentConversationSummary[];
}

/** GET /api/agent/conversations/:id — full transcript + metadata. */
export interface AgentConversationDetailResponse {
  conversation_id: string;
  agent_type: string;
  status: "active" | "closed";
  title?: string | null;
  messages: AgentHistoryMessage[];
}

/** POST /api/agent/conversations/:id/resume — confirms post-resume state. */
export interface AgentResumeResponse {
  conversation_id: string;
  agent_type: string;
  status: "active";
}

/**
 * Lists the caller's prior conversations, newest-message first. Default
 * limit on the server is 20; pass `limit` to override (server clamps to
 * 100). Returns `{ conversations: [] }` when the user has no history yet.
 */
export async function fetchConversationList(
  token: string,
  options?: { agentType?: string; limit?: number; signal?: AbortSignal },
): Promise<AgentConversationListResponse> {
  const agentType = options?.agentType ?? "readiness-advisor";
  const params = new URLSearchParams({ type: agentType });
  if (options?.limit != null) {
    params.set("limit", String(options.limit));
  }
  return apiRequest<AgentConversationListResponse>(
    `/api/agent/conversations?${params.toString()}`,
    { token, signal: options?.signal },
  );
}

/**
 * Fetches a single conversation + messages by id. The conversation may be
 * either active or closed; the sidebar uses this for previewing a closed
 * conversation before resume, and is also the post-resume hydration path.
 */
export async function fetchConversation(
  token: string,
  conversationID: string,
  signal?: AbortSignal,
): Promise<AgentConversationDetailResponse> {
  return apiRequest<AgentConversationDetailResponse>(
    `/api/agent/conversations/${encodeURIComponent(conversationID)}`,
    { token, signal },
  );
}

/**
 * Marks the named conversation active for the caller and closes any other
 * active conversation for the same (user, agent_type). The server also
 * invalidates the per-user session cache so the next chat turn rebuilds
 * the system prompt against the resumed conversation. Callers should re-
 * hydrate the chat surface from the returned response (or fetchConversation)
 * before letting the user type into the WebSocket.
 */
export async function resumeConversation(
  token: string,
  conversationID: string,
  signal?: AbortSignal,
): Promise<AgentResumeResponse> {
  return apiRequest<AgentResumeResponse>(
    `/api/agent/conversations/${encodeURIComponent(conversationID)}/resume`,
    { method: "POST", token, signal },
  );
}
